import type { Attributes, Span } from '@opentelemetry/api';
import { SpanStatusCode, trace } from '@opentelemetry/api';

/**
 * Use for meaningful trace boundaries (e.g. auth, session, init, key data steps).
 * For minimal diffs when adding/removing tracing, keep the callback thin:
 * `withServerSpan(name, () => impl(...))` with the real logic in a separate impl,
 * then export the wrapped boundary under the original public name when practical.
 * GQL calls are already traced inside the network helper; no need to wrap every fetch.
 * Default is async (returns Promise); use async: false only when a sync boundary is required.
 */
type ServerSpanOptions = {
  attributes?: Attributes;
  tracerName?: string;
  /** Default true. When false, runs the callback synchronously and returns T. */
  async?: boolean;
};

type ServerActionSpanOptions = {
  attributes?: Attributes;
  tracerName?: string;
};

/**
 * Names the custom BTS UI server instrumentation scope.
 * Trace membership comes from active context; span names carry operation meaning.
 */
const defaultTracerName = 'bts-ui-server';

const getServerTracer = (tracerName?: string) =>
  trace.getTracer(
    tracerName || defaultTracerName,
    process.env.NEXT_PUBLIC_APP_VERSION || 'dev',
  );

/**
 * Record exception on span and set status. If the error does not have a digest yet,
 * the span is ended in a deferred callback so we can pick up the digest after Next.js
 * mutates the error (Next adds err.digest in its RSC error handler).
 */
function recordExceptionAndSetStatus(
  span: Span,
  error: unknown,
  endSpanInFinally: { current: boolean },
): never {
  const err = error as Error & { digest?: string };

  span.recordException(err);

  if (err.digest != null) {
    span.setAttribute('exception.digest', err.digest);
  }

  span.setStatus({
    code: SpanStatusCode.ERROR,
    message: err?.message ?? '',
  });

  // Next.js adds digest in its error handler after we rethrow. Defer ending the span
  // so we can set exception.digest once Next has mutated the error object.
  // queueMicrotask runs after the current task (so after Next has run and set err.digest),
  // before the next macrotask, so the span is updated and ended before the request context may tear down.
  if (err.digest == null) {
    endSpanInFinally.current = false;

    queueMicrotask(() => {
      if (err.digest != null) {
        span.setAttribute('exception.digest', err.digest);
      }

      span.end();
    });
  }

  throw error;
}

export function withServerSpan<T>(
  name: string,
  fn: (span: Span) => T,
  options: ServerSpanOptions & { async: false },
): T;

export function withServerSpan<T>(
  name: string,
  fn: (span: Span) => T | Promise<T>,
  options?: ServerSpanOptions,
): Promise<T>;

export function withServerSpan<T>(
  name: string,
  fn: (span: Span) => T | Promise<T>,
  options?: ServerSpanOptions,
): T | Promise<T> {
  const tracer = getServerTracer(options?.tracerName);

  const isAsync = options?.async !== false;

  return tracer.startActiveSpan(name, (span) => {
    if (options?.attributes) {
      span.setAttributes(options.attributes);
    }

    const endSpanInFinally = { current: true };

    if (isAsync) {
      return (async () => {
        try {
          return await fn(span);
        } catch (error) {
          recordExceptionAndSetStatus(span, error, endSpanInFinally);
        } finally {
          // finally runs on both success and when recordExceptionAndSetStatus throws. When we
          // deferred (error had no digest), endSpanInFinally.current is false and the callback will end the span.
          if (endSpanInFinally.current) {
            span.end();
          }
        }
      })();
    }

    try {
      return fn(span) as T;
    } catch (error) {
      recordExceptionAndSetStatus(span, error, endSpanInFinally);
    } finally {
      // finally runs on both success and when recordExceptionAndSetStatus throws. When we
      // deferred (error had no digest), endSpanInFinally.current is false and the callback will end the span.
      if (endSpanInFinally.current) {
        span.end();
      }
    }
  });
}

/**
 * Wraps a Next.js Server Action in a stable `server.action` span.
 *
 * Keep the tracing opaque to callers by leaving the action body unexported, wrapping
 * it in a private `*WithSpan` const, and exporting that wrapper under the original
 * action name - see acceptFinalCheckWithSpan as an example
 */
export function withServerActionSpan<T>(
  actionName: string,
  fn: (span: Span) => Promise<T>,
  options?: ServerActionSpanOptions,
): Promise<T> {
  return withServerSpan('server.action', fn, {
    ...options,
    attributes: {
      'server.action.name': actionName,
      ...options?.attributes,
    },
  });
}
